/**
 * SecureEmbed Energy Portal - CorrelationChart Component
 *
 * Cross-datasource correlation chart (Energy + Weather, Regulatory + Energy)
 * Includes scatter plot with linear regression for weather-energy correlation
 */

import React from 'react';
import {
  ComposedChart, Line, Area, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, ReferenceLine, Legend,
  ScatterChart, Scatter, ZAxis
} from 'recharts';
import { CHART_COLORS, RECHARTS_THEME } from '../constants/colors';

interface CorrelationDataPoint {
  date: string;
  price?: number;
  temperature?: number;
  demand?: number;
}

interface CorrelationChartProps {
  data: CorrelationDataPoint[];
  type: 'energy-weather' | 'regulatory-energy';
  correlationCoeff?: number;
  regulatoryDates?: string[];
  region?: string;
  showScatter?: boolean;
}

function linearRegression(points: Array<{ x: number; y: number }>): {
  slope: number;
  intercept: number;
  rSquared: number;
} {
  const n = points.length;
  if (n < 2) return { slope: 0, intercept: 0, rSquared: 0 };

  let sumX = 0, sumY = 0, sumXY = 0, sumX2 = 0, sumY2 = 0;
  for (const p of points) {
    sumX += p.x;
    sumY += p.y;
    sumXY += p.x * p.y;
    sumX2 += p.x * p.x;
    sumY2 += p.y * p.y;
  }

  const denom = n * sumX2 - sumX * sumX;
  if (denom === 0) return { slope: 0, intercept: sumY / n, rSquared: 0 };

  const slope = (n * sumXY - sumX * sumY) / denom;
  const intercept = (sumY - slope * sumX) / n;

  const ssRes = points.reduce((s, p) => s + Math.pow(p.y - (slope * p.x + intercept), 2), 0);
  const meanY = sumY / n;
  const ssTot = points.reduce((s, p) => s + Math.pow(p.y - meanY, 2), 0);
  const rSquared = ssTot === 0 ? 0 : 1 - ssRes / ssTot;

  return { slope, intercept, rSquared };
}

const CorrelationChart: React.FC<CorrelationChartProps> = ({
  data,
  type,
  correlationCoeff,
  regulatoryDates = [],
  region,
  showScatter = false,
}) => {
  if (data.length === 0) {
    return (
      <div className="h-64 flex items-center justify-center text-gray-400">
        <p>No correlation data available</p>
      </div>
    );
  }

  const scatterData = data
    .filter(d => d.temperature != null && d.price != null)
    .map(d => ({ x: d.temperature!, y: d.price! }));

  const regression = scatterData.length >= 2 ? linearRegression(scatterData) : null;

  const regressionLine = regression && scatterData.length >= 2 ? (() => {
    const xMin = Math.min(...scatterData.map(p => p.x));
    const xMax = Math.max(...scatterData.map(p => p.x));
    return [
      { x: xMin, y: regression.slope * xMin + regression.intercept },
      { x: xMax, y: regression.slope * xMax + regression.intercept },
    ];
  })() : [];

  return (
    <div>
      {region && (
        <div className="mb-2">
          <span className="text-xs font-medium uppercase tracking-wider px-2 py-0.5 bg-gray-100 text-gray-600 rounded">
            {region}
          </span>
        </div>
      )}

      {correlationCoeff != null && (
        <div className="flex items-center gap-3 mb-4">
          <span className="text-sm text-gray-500">Pearson Correlation:</span>
          <span className={`text-lg font-bold ${
            Math.abs(correlationCoeff) >= 0.7 ? 'text-green-600' :
            Math.abs(correlationCoeff) >= 0.4 ? 'text-amber-600' : 'text-gray-600'
          }`}>
            r = {correlationCoeff.toFixed(3)}
          </span>
          <span className="text-xs text-gray-400">
            ({Math.abs(correlationCoeff) >= 0.7 ? 'Strong' :
              Math.abs(correlationCoeff) >= 0.4 ? 'Moderate' : 'Weak'} correlation)
          </span>
        </div>
      )}

      <ResponsiveContainer width="100%" height={320}>
        <ComposedChart data={data} margin={{ left: 10, right: 10 }}>
          <CartesianGrid {...RECHARTS_THEME.grid} />
          <XAxis dataKey="date" tick={RECHARTS_THEME.label} />
          {type === 'energy-weather' ? (
            <>
              <YAxis yAxisId="left" tick={RECHARTS_THEME.label} label={{ value: '$/MWh', angle: -90, position: 'insideLeft', style: { fontSize: 11, fill: '#6B7280' } }} />
              <YAxis yAxisId="right" orientation="right" tick={RECHARTS_THEME.label} label={{ value: 'Temperature (C)', angle: 90, position: 'insideRight', style: { fontSize: 11, fill: '#6B7280' } }} />
              <Area yAxisId="left" type="monotone" dataKey="price" fill={CHART_COLORS.aemo + '20'} stroke={CHART_COLORS.aemo} strokeWidth={2} name="Energy Price ($/MWh)" />
              <Line yAxisId="right" type="monotone" dataKey="temperature" stroke={CHART_COLORS.bom} strokeWidth={2} dot={false} name="Temperature (C)" />
            </>
          ) : (
            <>
              <YAxis yAxisId="left" tick={RECHARTS_THEME.label} />
              <Area yAxisId="left" type="monotone" dataKey="price" fill={CHART_COLORS.aemo + '20'} stroke={CHART_COLORS.aemo} strokeWidth={2} name="Energy Price ($/MWh)" />
              {regulatoryDates.map((date, i) => (
                <ReferenceLine key={i} x={date} yAxisId="left" stroke={CHART_COLORS.aer} strokeWidth={2} strokeDasharray="5 5" label={{ value: 'AER', fill: CHART_COLORS.aer, fontSize: 10, position: 'top' }} />
              ))}
            </>
          )}
          <Tooltip contentStyle={RECHARTS_THEME.tooltip.contentStyle} />
          <Legend />
        </ComposedChart>
      </ResponsiveContainer>

      {showScatter && type === 'energy-weather' && scatterData.length >= 2 && (
        <div className="mt-6">
          <h4 className="text-sm font-semibold text-gray-700 mb-2">Temperature vs Price Scatter Plot</h4>
          <ResponsiveContainer width="100%" height={280}>
            <ScatterChart margin={{ left: 10, right: 10, bottom: 10 }}>
              <CartesianGrid {...RECHARTS_THEME.grid} />
              <XAxis type="number" dataKey="x" name="Temperature" tick={RECHARTS_THEME.label} label={{ value: 'Temperature (\u00B0C)', position: 'insideBottom', offset: -5, style: { fontSize: 11, fill: '#6B7280' } }} />
              <YAxis type="number" dataKey="y" name="Price" tick={RECHARTS_THEME.label} label={{ value: '$/MWh', angle: -90, position: 'insideLeft', style: { fontSize: 11, fill: '#6B7280' } }} />
              <ZAxis range={[40, 40]} />
              <Tooltip contentStyle={RECHARTS_THEME.tooltip.contentStyle} formatter={(value: number, name: string) => [`${value.toFixed(1)}`, name === 'x' ? 'Temperature (\u00B0C)' : '$/MWh']} />
              <Scatter data={scatterData} fill={CHART_COLORS.aemo} opacity={0.7} />
              {regressionLine.length === 2 && (
                <Scatter data={regressionLine} fill="none" line={{ stroke: CHART_COLORS.primary, strokeWidth: 2, strokeDasharray: '5 5' }} shape={() => <></>} />
              )}
            </ScatterChart>
          </ResponsiveContainer>
          {regression && (
            <div className="mt-2 flex items-center gap-4 text-sm">
              <span className="text-gray-500">Regression: y = {regression.slope.toFixed(2)}x + {regression.intercept.toFixed(1)}</span>
              <span className={`font-bold ${regression.rSquared >= 0.5 ? 'text-green-600' : 'text-amber-600'}`}>R&sup2; = {regression.rSquared.toFixed(3)}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CorrelationChart;
